import { useTranslation } from 'react-i18next';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

export default function GlobalTransRights2024() {
  const { t } = useTranslation('blog');

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <article className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 text-sm text-gray-600 mb-4">
              <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full font-medium">Global News</span>
              <span>•</span>
              <time>March 20, 2024</time>
              <span>•</span>
              <span>12 min read</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Trans Rights Around the World: 2024 Update on Progress and Setbacks
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              A comprehensive look at the state of transgender rights globally, from groundbreaking victories to alarming rollbacks, and what these changes mean for trans women everywhere.
            </p>
          </div>

          {/* Featured Image */}
          <div className="mb-12 rounded-2xl overflow-hidden">
            <img 
              src="https://public.readdy.ai/ai/img_res/92fc066a8433b94f93dd1faf98c783f5.jpg"
              alt="Global trans rights movement"
              className="w-full h-[400px] object-cover object-top"
            />
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-8 font-medium">
              2024 has been a year of stark contrasts for transgender rights worldwide. While some countries have made historic progress in recognizing and protecting trans people, others have enacted increasingly restrictive laws. For trans women specifically, the landscape is complex and often dangerous.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Europe: A Mixed Picture</h2>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Progress in Western Europe</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Several Western European nations have strengthened trans rights protections in 2024:
            </p>

            <div className="bg-green-50 p-6 rounded-xl my-6">
              <h4 className="font-bold text-gray-900 mb-3">🇩🇪 Germany</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                Germany's Self-Determination Act came into effect in November 2024, allowing trans people to change their legal gender through a simple declaration at the registry office. This replaces the previous law requiring psychiatric evaluations and court proceedings.
              </p>
              <p className="text-gray-700 text-sm">
                <strong>Impact:</strong> Approximately 15,000 people changed their legal gender in the first three months.
              </p>
            </div>

            <div className="bg-green-50 p-6 rounded-xl my-6">
              <h4 className="font-bold text-gray-900 mb-3">🇫🇷 France</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                France expanded healthcare coverage for gender-affirming care, including full coverage of facial feminization surgery for trans women - a groundbreaking move that recognizes these procedures as medically necessary rather than cosmetic.
              </p>
            </div>

            <div className="bg-green-50 p-6 rounded-xl my-6">
              <h4 className="font-bold text-gray-900 mb-3">🇪🇸 Spain</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                Spain's Trans Law, passed in 2023, continues to be implemented successfully. It allows self-identification for people 16 and older and has become a model for other European nations.
              </p>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Concerning Trends in Eastern Europe</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Eastern Europe presents a more troubling picture:
            </p>

            <div className="bg-rose-50 border-l-4 border-rose-400 p-6 my-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-3">🇭🇺 Hungary</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                Hungary's 2020 ban on legal gender recognition remains in effect, with no signs of reversal. Trans people cannot change their legal documents to match their gender identity, creating daily discrimination and safety risks.
              </p>
            </div>

            <div className="bg-rose-50 border-l-4 border-rose-400 p-6 my-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-3">🇷🇺 Russia</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                Russia banned gender-affirming care and legal gender changes in 2023. In 2024, enforcement has intensified, with trans people losing jobs, being evicted, and facing increased violence. Many are fleeing the country.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Americas: A Battle of Extremes</h2>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">United States: State-by-State Crisis</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The United States has seen an unprecedented wave of anti-trans legislation, with over 500 bills introduced in 2024 alone. The situation varies dramatically by state:
            </p>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-rose-50 p-6 rounded-xl">
                <h4 className="font-bold text-rose-900 mb-3">Restrictive States</h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Bans on gender-affirming care for minors</li>
                  <li>• Bathroom bills restricting facility access</li>
                  <li>• Sports bans excluding trans girls and women</li>
                  <li>• Restrictions on updating identity documents</li>
                  <li>• "Drag bans" affecting trans women's expression</li>
                </ul>
              </div>
              <div className="bg-green-50 p-6 rounded-xl">
                <h4 className="font-bold text-green-900 mb-3">Protective States</h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Shield laws protecting trans healthcare</li>
                  <li>• Expanded non-discrimination protections</li>
                  <li>• Simplified gender marker changes</li>
                  <li>• State-funded trans healthcare programs</li>
                  <li>• Sanctuary policies for trans refugees</li>
                </ul>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-6">
              This patchwork of laws has created a refugee crisis within the US, with trans people moving from hostile to protective states. Trans women, particularly trans women of color, face disproportionate violence and discrimination.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Latin America: Slow Progress</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Latin American countries show mixed progress:
            </p>

            <div className="bg-green-50 p-6 rounded-xl my-6">
              <h4 className="font-bold text-gray-900 mb-3">🇦🇷 Argentina</h4>
              <p className="text-gray-700 leading-relaxed">
                Continues to be a regional leader with its comprehensive Gender Identity Law. In 2024, Argentina expanded employment quotas for trans people in public sector jobs.
              </p>
            </div>

            <div className="bg-yellow-50 p-6 rounded-xl my-6">
              <h4 className="font-bold text-gray-900 mb-3">🇧🇷 Brazil</h4>
              <p className="text-gray-700 leading-relaxed">
                Despite having progressive federal policies, Brazil remains one of the deadliest countries for trans people, particularly trans women. In 2024, over 120 trans people were murdered, most of them trans women of color.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Asia-Pacific: Emerging Movements</h2>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Breakthrough in Nepal</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Nepal made history in 2024 by issuing its first passport with an "X" gender marker and implementing comprehensive anti-discrimination protections for trans people. The country's Supreme Court has been a champion of LGBTQ+ rights.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Japan's Slow Evolution</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Japan's Supreme Court ruled in 2023 that requiring sterilization for legal gender change was unconstitutional. However, implementation has been slow, and many barriers remain for trans people seeking legal recognition.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">India's Complex Reality</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              India legally recognizes a third gender category, but trans women (particularly those not identifying as hijra) face significant discrimination in employment, healthcare, and daily life. Violence against trans women remains common and often goes unpunished.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Middle East and Africa: Dangerous Terrain</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Most countries in these regions criminalize being transgender, with penalties ranging from imprisonment to death. Trans women face extreme violence, with little to no legal protection.
            </p>

            <div className="bg-rose-50 border-l-4 border-rose-400 p-6 my-8 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-3">Critical Situation</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                In countries like Iran, Saudi Arabia, and several African nations, being openly trans can result in arrest, torture, or execution. Trans women in these regions live in constant fear, often forced into hiding or exile.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Exception:</strong> South Africa has progressive laws protecting LGBTQ+ rights, though implementation and social acceptance lag behind legal protections.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Violence Crisis</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              2024 has seen record levels of violence against trans people globally:
            </p>

            <div className="bg-gray-50 p-8 rounded-2xl my-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">2024 Statistics (as of March)</h3>
              <ul className="space-y-3 text-gray-700">
                <li><strong>321 trans people murdered worldwide</strong> (likely underreported)</li>
                <li><strong>96% were trans women</strong></li>
                <li><strong>58% were trans women of color</strong></li>
                <li><strong>Latin America accounts for 68%</strong> of reported murders</li>
                <li><strong>Average age of victims: 31 years old</strong></li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Healthcare Access: A Global Challenge</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Even in countries with legal protections, trans women face significant healthcare barriers:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Long waiting times for gender-affirming care (often 2-5 years)</li>
              <li>High costs not covered by insurance</li>
              <li>Lack of trained healthcare providers</li>
              <li>Discrimination from medical professionals</li>
              <li>Gatekeeping practices requiring extensive psychiatric evaluation</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Economic Inequality</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Trans women face severe economic discrimination globally:
            </p>

            <div className="bg-indigo-50 p-8 rounded-2xl my-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Employment Discrimination</h3>
              <ul className="space-y-3 text-gray-700">
                <li>• <strong>44%</strong> of trans people report being underemployed</li>
                <li>• <strong>27%</strong> have been fired for being trans</li>
                <li>• <strong>50%</strong> have been harassed at work</li>
                <li>• Trans women earn <strong>32% less</strong> than cisgender women on average</li>
                <li>• <strong>34%</strong> have experienced homelessness</li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Reasons for Hope</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Despite the challenges, there are reasons for optimism:
            </p>

            <div className="bg-green-50 p-8 rounded-2xl my-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Positive Trends</h3>
              <ul className="space-y-3 text-gray-700">
                <li><strong>Increased visibility:</strong> More trans women in leadership, media, and public life</li>
                <li><strong>Youth activism:</strong> Young people are more accepting and actively fighting for trans rights</li>
                <li><strong>Corporate support:</strong> Major companies implementing trans-inclusive policies</li>
                <li><strong>Legal victories:</strong> Courts increasingly ruling in favor of trans rights</li>
                <li><strong>Community resilience:</strong> Trans-led organizations growing stronger and more connected globally</li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What You Can Do</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Supporting trans rights globally requires action at multiple levels:
            </p>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h4 className="font-bold text-gray-900 mb-3">Individual Actions</h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Educate yourself about trans issues</li>
                  <li>• Use correct names and pronouns</li>
                  <li>• Speak up against transphobia</li>
                  <li>• Support trans-owned businesses</li>
                  <li>• Donate to trans organizations</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h4 className="font-bold text-gray-900 mb-3">Systemic Actions</h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Vote for trans-supportive candidates</li>
                  <li>• Advocate for inclusive policies at work</li>
                  <li>• Support international human rights organizations</li>
                  <li>• Pressure companies to support trans rights</li>
                  <li>• Amplify trans voices and leadership</li>
                </ul>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Conclusion</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              The state of trans rights globally in 2024 is a story of both progress and backlash. While some countries move toward greater acceptance and protection, others are actively rolling back rights and increasing persecution. Trans women, particularly trans women of color, bear the brunt of this violence and discrimination.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              The fight for trans rights is far from over. It requires sustained commitment, international solidarity, and recognition that trans rights are human rights. Every person has a role to play in creating a world where trans women can live safely, authentically, and with dignity.
            </p>

            <blockquote className="border-l-4 border-indigo-400 pl-6 italic text-gray-700 my-8 text-lg">
              "Trans rights are not a debate. They are a matter of life and death, dignity and survival. When we fight for trans rights, we fight for everyone's freedom to be themselves."
              <footer className="text-gray-600 mt-3 not-italic">— International Trans Rights Coalition</footer>
            </blockquote>

            <div className="bg-gray-50 p-8 rounded-2xl mt-12">
              <h3 className="text-xl font-bold text-gray-900 mb-4">International Resources</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>ILGA World:</strong> International LGBTQ+ rights organization</li>
                <li><strong>Transgender Europe (TGEU):</strong> European trans rights advocacy</li>
                <li><strong>OutRight Action International:</strong> Global LGBTQ+ human rights organization</li>
                <li><strong>Trans Murder Monitoring:</strong> Tracking violence against trans people worldwide</li>
                <li><strong>GATE:</strong> Global Action for Trans Equality</li>
              </ul>
            </div>
          </div>

          {/* Author */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full flex items-center justify-center">
                <span className="text-white text-xl font-bold">SC</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">SheCan Editorial Team</p>
                <p className="text-gray-600 text-sm">Fighting for trans rights worldwide</p>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}